/* eslint-disable max-classes-per-file */
/* eslint-disable max-lines */
import { Action } from 'redux'
import { ThunkDispatch } from 'redux-thunk'
import type { DeepReadonly } from 'utility-types'

import {
  ActiveMilitaryOptions,
  ApiProductType,
  BooleanOptions,
  Brand,
  CitizenshipOptions,
  ClientTags,
  ConfigServiceDataKeys,
  CreditCardFlow,
  CreditCardPurposesOptions,
  CreditScoreOptions,
  DepositProductTypesOptions,
  EducationLevelOptions,
  EmploymentStatusOptions,
  EventScript,
  Filter,
  GenderTypeOptions,
  GoogleAnalyticsActions,
  HeightTypeOptions,
  LifeInsuranceQuestion,
  LifeInsuranceQuestionType,
  LoanFlow,
  LoanPurposeOptions,
  LoanStep,
  MortgageTypeOptions,
  NumberOfUnitsOptions,
  OccupancyTypeOptions,
  PayFrequencyOptions,
  Product,
  PropertyStatusOptions,
  PropertyTypeOptions,
  PurchaseStatusOptions,
  QueryParamKeys,
  RewardsPreferenceTypeOptions,
  Theme,
  TobaccoSmokerOptions,
  VehicleIdTypeOptions,
} from '@evenfinancial/even-ts-static'
import createAutoCorrectedDatePipe from 'text-mask-addons/dist/createAutoCorrectedDatePipe'
import createNumberMask from 'text-mask-addons/dist/createNumberMask'
import { CommonState, MultiProductState } from 'embed/reducers'

export enum DirectDepositOptions {
  YES = 'yes',
  NO = 'no',
}

// Since we're now targeting ES6, we can't use import assignments anymore, hence this
// monkey patch of interface in node_modules/@types/googlemaps/reference/geocoder.d.ts.
interface GeocoderResult {
  address_components: {
    long_name: string
    short_name: string
    types: string[]
  }[]
  formatted_address: string
  geometry: {
    bounds: never
    location: never
    location_type: never
    viewport: never
  }
  partial_match: boolean
  place_id: string
  postcode_localities: string[]
  types: string
}

import QueryParams from 'embed/services/QueryParams'
import { ValidationData, Validator } from 'embed/services/Validation'
import {
  CreditOffer,
  DepositsOffer,
  LifeInsuranceOffer,
  LoanOffer,
  MortgageOffer,
  SpecialOffer,
} from 'embed/constants/models'
import { MaskedInputProps } from 'react-text-mask'
import { isNil } from 'embed/utils/object'

export const UI_NAME = 'multi-product'

export const CARD_PURPOSES_URL_KEY = 'app.cardPurposes'
export const RESULTS_URL_KEY = 'results'
export const OFFER_URL_KEY = 'offer'

export const BESTOW_UUID_DEV = 'ffd6a014-4684-4dd9-b8a6-5be824da682c'
export const BESTOW_UUID_PROD = '26eb2c35-1a26-4ec9-8608-103a6f08d83b'

export const LEGAL_GENERAL_UUID_DEV = '976c4fe7-b2cb-4878-b6d7-d5ff8d81f5c2'
export const LEGAL_GENERAL_UUID_PROD = '198f5e46-b5e8-454f-81bd-1741bcf97637'

export const HAVEN_TERM_UUID_DEV = 'a67ea738-98af-40ce-a01e-3ac740ddfbde'
export const HAVEN_SIMPLE_UUID_DEV = 'f3ddfb35-1822-4127-820a-f70578e1ecac'
export const HAVEN_TERM_UUID_PROD = '6289fb77-d8c6-42a5-84ef-490dd0043bb0'
export const HAVEN_SIMPLE_UUID_PROD = 'fe65c29e-9ece-46a8-a6ee-48334bc7b146'

export const MOO_COMPANY_UUID_PROD = 'bd78cda4-eb09-45d8-9fb8-9619f668e979'
export const MOO_COMPANY_UUID_DEV = '0a846150-29fa-43b7-a845-8829bc47f7f7'

export const BESTOW_COMPANY_UUID_PROD = '26eb2c35-1a26-4ec9-8608-103a6f08d83b'
export const BESTOW_COMPANY_UUID_DEV = 'ffd6a014-4684-4dd9-b8a6-5be824da682c'

export const THE_STANDARD_UUID_DEV = '1ea144e1-cc81-496f-b7d6-58aeb75df76a'
export const THE_STANDARD_UUID_PROD = '3f23d75a-9dcb-49c7-baa8-398fbfb04f64'

export const EVEN_REQUEST_REDIRECT_EVENT = 'even_request_redirect'
export const EVEN_VIEWPORT_UPDATE_EVENT = 'even_viewport_update'

export const DEPOSITS_AD_DISCLOSURE_COPY = `The offers that appear are from companies which Engine by MoneyLion and its partners receive compensation.
 This compensation will influence the selection, appearance, and order of appearance of the offers listed below.
 However, this compensation also facilitates the provision by Engine by MoneyLion of certain services to you at no charge.
 The offers displayed in your results do not include all financial services companies or all of their available product and service offerings.`

export type MultiProductDispatch = ThunkDispatch<MultiProductState, void, Action>

export enum ReturnToSearchText {
  Back = 'Back',
  Default = 'Return to search',
}

export interface ViewportUpdateMessageBody {
  childRect: {
    bottom: number
    height: number
    left: number
    right: number
    top: number
    width: number
  }
  parentRect: {
    height: number
    width: number
  }
  automatedScrolling: boolean
}

export enum CoApplicantAddressMatch {
  DifferentFromApplicant = 'differentFromApplicant',
  SameAsApplicant = 'sameAsApplicant',
}

// ratetable offer productSubType values
export enum LoanOfferProductSubType {
  AutomobileRefinance = 'automobile_refinance',
  DebtRelief = 'debt_relief',
  LineOfCredit = 'line_of_credit',
  PersonalLoan = 'personal_loan',
  SecuredLoan = 'secured_loan',
  StudentLoanRefinance = 'student_loan_refinance',
  HomeEquityLineOfCredit = 'home_equity_line_of_credit',
  Unknown = 'unknown',
}

export enum DepositsOfferProductSubType {
  Cma = 'cash_management_account',
  Che = 'checking',
  Ira = 'individual_retirement_account',
  Mma = 'money_market_account',
  Savings = 'savings_account',
}

export const LenderNameKeys = {
  [Product.Deposits]: 'partner.name',
  [Product.Loan]: 'originator.name',
}

export enum LoanRatesFilter {
  AprType = 'aprType',
  ProductSubType = 'productSubType',
}

export enum LoanFilterTypes {
  LENDERS = 'lenders',
}

export enum LifeInsuranceRatesFilter {
  CoverageTier = 'coverageTier',
}

export enum LifeInsuranceCoverageTier {
  Basic = 'basic',
  Essential = 'essential',
  Elite = 'elite',
}

export const LifeInsuranceCoverageAmounts = [
  100000, 250000, 350000, 500000, 600000, 750000, 850000, 1000000, 1500000, 2000000, 2500000, 3000000, 4000000, 5000000,
] as const

export const HavenSimpleCoverageAmounts = [
  25000, 50000, 75000, 100000, 150000, 200000, 250000, 300000, 350000, 400000, 450000, 500000,
] as const

export const HavenTermCoverageAmounts = [
  100000, 150000, 200000, 250000, 300000, 350000, 400000, 450000, 500000, 550000, 600000, 650000, 700000, 750000,
  800000, 850000, 900000, 950000, 1000000,
] as const

export type LifeInsuranceCoverageAmount = typeof LifeInsuranceCoverageAmounts[keyof typeof LifeInsuranceCoverageAmounts]

export type HavenSimpleCoverageAmount = typeof HavenSimpleCoverageAmounts[keyof typeof HavenSimpleCoverageAmounts]

export type HavenTermCoverageAmount = typeof HavenTermCoverageAmounts[keyof typeof HavenTermCoverageAmounts]

export const LifeInsuranceDurations = [10, 15, 20, 30] as const

export const HavenTermDurations = [5, 10, 15] as const

export type LifeInsuranceDuration = typeof LifeInsuranceDurations[keyof typeof LifeInsuranceDurations]

export type HavenTermDuration = typeof HavenTermDurations[keyof typeof HavenTermDurations]

export type RatesFilter = LifeInsuranceRatesFilter | LoanRatesFilter

export enum AprType {
  Fixed = 'fixed',
  Variable = 'variable',
}

export enum AprTypeOption {
  Fixed = 'Fixed APR',
  Variable = 'Variable APR',
}

export type Mask = (string | RegExp)[]
export type MaskFunction = (value: unknown) => Mask

// eslint-disable-next-line @typescript-eslint/no-redeclare
export const Mask = {
  AnnualIncome: createNumberMask({ integerLimit: 7 }),
  AutomobileLoanBalance: createNumberMask({ integerLimit: 6 }),
  BankRoutingNumber: [/\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/],
  Currency: createNumberMask({ integerLimit: 9 }),
  Date: {
    Full: [/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/],
    MonthAndYearOnly: [/\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/],
  },
  EstimatedMileage: createNumberMask({
    integerLimit: 6,
    prefix: '',
  }),
  MonthlyPayment: createNumberMask({ integerLimit: 4 }),
  PhoneNumber: ['(', /\d/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/],
  Pipe: {
    Date: {
      Full: createAutoCorrectedDatePipe('mm/dd/yyyy'),
      MonthAndYearOnly: createAutoCorrectedDatePipe('mm/yyyy'),
    },
    State: (state: string) => state.toUpperCase(),
    Weight: (weight) => (weight ? `${weight} lbs` : weight),
  },
  SocialSecurityNumber: [/\d/, /\d/, /\d/, '-', /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/],
  State: [/[a-zA-Z]/, /[a-zA-Z]/],
  Weight: (input: string) => {
    const inputLength = input.replace(/\D/g, '').length
    return new Array(Math.min(inputLength, 4)).fill(/\d/)
  },
  ZipCode: (input: string) => {
    const inputLength = input.replace(/\D/g, '').length
    return inputLength > 5
      ? [/\d/, /\d/, /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/]
      : [/\d/, /\d/, /\d/, /\d/, /\d/]
  },
}

export interface Image {
  sizeKey: string
  url: string
}

export interface Originator {
  key: string
  name: string
  description: string
  images: Image[]
  disclaimer: string
  companyUuid: string
}

export type PendingResponse = DeepReadonly<{
  partner: PendingResponsePartner
  productTypes: string[]
}>

export interface PendingResponsePartner {
  uuid: string
  name: string
  description: string
  disclaimer: string
  supportsPersonalizedOffers: boolean
  supportsPreSelect: boolean
  shouldDisplayPreSelect: boolean
  imageUrl: string
}

export enum CoApplicantFormState {
  SHOWN = 'shown',
  HIDDEN = 'hidden',
}

export enum FieldType {
  AddressAutocomplete = 'addressAutocomplete',
  Toggle = 'toggle',
  ButtonList = 'buttonList',
  IconButtonList = 'iconButtonList',
  DynamicSelect = 'dynamicSelect',
  FreeformMultipleText = 'freeformMultipleText',
  Hidden = 'hidden', // Used for fields that the user doesn't interact with, but are sent as part of an application
  MultiButtonList = 'multiButtonList',
  Number = 'number',
  Password = 'password',
  Radio = 'radio',
  RadioGroup = 'radioGroup',
  Select = 'select',
  Text = 'text',
  UniversityAutocomplete = 'universityAutocomplete',
  Unknown = 'unknown',
  Zipcode = 'zipcode',
  SliderInput = 'sliderInput',
  PrescriptionAutocomplete = 'prescriptionAutocomplete',
}

// Definitions for services that can populate fields (e.g. zipcode can come from geoip, Plaid, or user entered).
// Each service has a priority, the higher the priority sources will override the lower ones.
// e.g. if Plaid has set the zipcode then later the geoip service attempts to set the zipcode it will
// not be changed since Plaid has a higher priority.

export interface FieldDataSource {
  id: string
  priority: number
}

export const FieldDataSources = {
  GeoIP: {
    id: 'geoip',
    priority: 1,
  },
  Plaid: {
    id: 'plaid',
    priority: 2,
  },
}

export interface FieldChangedOptions {
  dataSource?: FieldDataSource
  lenderName?: string
  userInitiated?: boolean
}

export type FieldsChangedOptions = {
  [f in Filter]?: FieldChangedOptions
}

export interface FieldData extends FieldChangedOptions {
  isLoading?: boolean
  shouldShowValidationStatus?: boolean
  isValid?: boolean
  populatedByPlaid?: boolean
  populatedByPlaidThenChanged?: boolean
  value?: FieldDataValue
}

export type FieldDataValue = string | string[] | number | boolean | undefined | DeepReadonly<string[]>

// TODO: Determine if Filter should be the canonical list of fields
export type FieldKey = Filter | string

export interface FieldSelectOption {
  label: string
  value: string
}

export interface FieldValidator {
  message?: string
  validator: Validator
}

export interface FieldOptions {
  maxAmount?: number | ((data: ValidationData) => number)
  canOverflowMaxAmount?: boolean
  minAmount?: number
  autocomplete?: string
  defaultValidationMessage?: string | ((data: ValidationData) => string)
  hasFloatingLabel?: boolean
  helperText?: string
  hideValidationStatus?: boolean
  initialValue?: FieldDataValue
  inputMode?: 'numeric' | 'text'
  label?: React.ReactNode
  placeholder?: string | ((data: ValidationData) => string)
  readOnly?: boolean
  required?: boolean
  sectionIsHiddenText?: string
  sectionIsShownText?: string
  tooltip?: string | (React.ReactNode & React.ReactChild)
  validators?: FieldValidator[]
}

export enum ButtonListFieldDirectionOption {
  Row = 'row',
  Column = 'column',
}

export interface ButtonListFieldOptions extends FieldOptions {
  pathToStore?: string
  selectOptions?: FieldSelectOption[]
  direction?: ButtonListFieldDirectionOption
}

export interface MultiButtonListFieldOptions extends ButtonListFieldOptions {
  allowNone?: boolean
}

export interface SelectFieldOptions extends FieldOptions {
  searchable?: boolean
  selectOptions?: FieldSelectOption[]
}

export interface RadioButton {
  checked?: boolean
  label: string
  name: string
  pathToStore?: string
  trackingEvent: RadioButtonTrackingEvent
  value: string
}

export interface RadioButtonTrackingEvent {
  action: GoogleAnalyticsActions | string
  data: CoApplicantFormState
}

export interface ToggleFieldTrackingEvent {
  action: GoogleAnalyticsActions | string
  shown: string
  hidden: string
}

export interface RadioGroupFieldOptions extends FieldOptions {
  label: string
  children: RadioButton[]
}

export interface DynamicSelectFieldOptions extends SelectFieldOptions {
  optionValuesConfigKey: ConfigServiceDataKeys
  optionBoundingSet: DropdownItem<unknown>[]
}

export interface SliderInputFieldOptions extends TextFieldOptions {
  minAmount?: number
  maxAmount?: number | ((data: ValidationData) => number)
}

export interface TextFieldOptions extends FieldOptions {
  mask?: Mask | MaskFunction
  pipe?: MaskedInputProps['pipe']
}

export class Field {
  readonly key: FieldKey
  readonly type: FieldType
  readonly options: FieldOptions

  constructor(key: FieldKey, type: FieldType) {
    this.key = key
    this.type = type

    this.validationMessage = this.validationMessage.bind(this)
  }

  // Determine if the field can be changed to the specified value
  canChangeValue(value: FieldDataValue, fieldData: FieldData, fieldChangedOptions?: FieldChangedOptions): boolean {
    // If the field is read only, it can only be set to it's initial value
    if (this.options.readOnly === true) {
      return this.options.initialValue === value
    }

    // If the current value and in the incoming value both have a priority set, only allow the incoming value to be set
    // if it's a higher value than the currently set one.
    // Note: This has the effect that any incoming value that doesn't have a priority set, will always be set (a safe default)

    // Adding comparison for equal priority for when the same field is changed multiple times
    // This fixes City/State not updating when zipcode is updated after first change
    if (fieldData?.dataSource?.priority !== undefined && fieldChangedOptions?.dataSource?.priority !== undefined) {
      return (
        fieldChangedOptions.dataSource?.priority > fieldData.dataSource.priority ||
        fieldChangedOptions.dataSource?.priority === fieldData.dataSource.priority
      )
    }

    return true
  }

  // Validation that returns false if the field is required but the value is empty
  requiredValidation(value: FieldDataValue) {
    return !(this.options.required && (isNil(value) || value === ''))
  }

  // Validation that returns false if one of the provided custom validators returns false
  customValidations(value: FieldDataValue, validationData?: ValidationData) {
    if (this.options.validators === undefined) {
      return true
    }

    for (const fieldValidator of this.options.validators) {
      if (fieldValidator.validator.isValid(value, validationData) === false) {
        return false
      }
    }

    return true
  }

  // Run all validations (required & custom)
  validate(value: FieldDataValue, validationData?: ValidationData): boolean {
    return this.requiredValidation(value) && this.customValidations(value, validationData)
  }

  validationMessage(value: FieldDataValue, validationData: ValidationData) {
    const defaultValidationMessage =
      typeof this.options.defaultValidationMessage === 'function'
        ? this.options.defaultValidationMessage(validationData)
        : this.options.defaultValidationMessage

    if (this.requiredValidation(value) === false) {
      return defaultValidationMessage
    }

    if (this.options.validators) {
      for (const fieldValidator of this.options.validators) {
        if (fieldValidator.validator.isValid(value, validationData) === false) {
          // If a custom validator is invalid, return it's custom validation message if it exists or fall back to the default message
          return fieldValidator.message !== undefined ? fieldValidator.message : defaultValidationMessage
        }
      }
    }
  }
}

export class HiddenField extends Field {
  readonly options: FieldOptions

  constructor(key: FieldKey, options: FieldOptions) {
    super(key, FieldType.Hidden)
    this.options = options
  }
}

export class ButtonListField extends Field {
  readonly options: ButtonListFieldOptions

  constructor(key: FieldKey, options: ButtonListFieldOptions) {
    super(key, FieldType.ButtonList)
    this.options = options
  }
}

export class IconButtonListField extends Field {
  readonly options: ButtonListFieldOptions

  constructor(key: FieldKey, options: ButtonListFieldOptions) {
    super(key, FieldType.IconButtonList)
    this.options = options
  }
}

export class MultiButtonListField extends Field {
  readonly options: MultiButtonListFieldOptions

  constructor(key: FieldKey, options: MultiButtonListFieldOptions) {
    super(key, FieldType.MultiButtonList)
    this.options = options
  }
}

export class CheckboxField extends Field {
  readonly options: FieldOptions

  constructor(key: FieldKey, options: FieldOptions) {
    super(key, FieldType.Select)
    this.options = { hasFloatingLabel: true, ...options }
  }
}

export class SelectField extends Field {
  readonly options: SelectFieldOptions

  constructor(key: FieldKey, options: SelectFieldOptions) {
    super(key, FieldType.Select)
    this.options = { hasFloatingLabel: true, ...options }
  }
}

export class DynamicSelectField extends Field {
  readonly options: DynamicSelectFieldOptions

  constructor(key: FieldKey, options: DynamicSelectFieldOptions) {
    super(key, FieldType.DynamicSelect)
    this.options = { hasFloatingLabel: true, ...options }
  }
}

export class NumberField extends Field {
  readonly options: TextFieldOptions

  constructor(key: FieldKey, options: TextFieldOptions) {
    super(key, FieldType.Number)
    this.options = options
  }
}

export class RadioGroupField extends Field {
  readonly options: RadioGroupFieldOptions

  constructor(key: FieldKey, options: RadioGroupFieldOptions) {
    super(key, FieldType.RadioGroup)
    this.options = options
  }
}

export class TextField extends Field {
  readonly options: TextFieldOptions

  constructor(key: FieldKey, options: TextFieldOptions, type: FieldType = FieldType.Text) {
    super(key, type)
    this.options = { hasFloatingLabel: true, ...options }
  }
}

export class SliderInput extends Field {
  readonly options: SliderInputFieldOptions

  constructor(key: FieldKey, options: SliderInputFieldOptions, type: FieldType = FieldType.SliderInput) {
    super(key, type)
    this.options = options
  }
}

export class FreeformMultipleText extends TextField {
  constructor(key: FieldKey, options: TextFieldOptions) {
    super(key, options, FieldType.FreeformMultipleText)
  }
}

export class PrescriptionAutocomplete extends TextField {
  constructor(key: FieldKey, options: TextFieldOptions) {
    super(key, options, FieldType.PrescriptionAutocomplete)
  }
}

export class AddressAutocomplete extends TextField {
  constructor(key: FieldKey, options: TextFieldOptions) {
    super(key, { hasFloatingLabel: true, ...options }, FieldType.AddressAutocomplete)
  }
}

export class UniversityAutocomplete extends TextField {
  constructor(key: FieldKey, options: TextFieldOptions) {
    super(key, { hasFloatingLabel: true, ...options }, FieldType.UniversityAutocomplete)
  }
}

export class ZipcodeField extends TextField {
  constructor(key: FieldKey, options: TextFieldOptions) {
    super(key, options, FieldType.Zipcode)
  }
}

export type ApplicationFields = { [K in FieldKey]: FieldData }

export interface Application {
  fields?: ApplicationFields
  isValid?: boolean
}

export const ThemeKeys: (keyof Theme)[] = [
  'backdropStyles',
  'borderRadiusLarge',
  'borderRadiusSmall',
  'colorBackgroundSecondary',
  'colorBorder',
  'colorCta',
  'colorError',
  'colorPrimary',
  'colorSecondary',
  'colorTextPrimary',
  'colorTextSecondary',
  'ctaStyles',
  'errorStyles',
  'fontFamilyCTA',
  'fontFamilyHeadline',
  'fontFamilyLabels',
  'fontFamilyPrimary',
  'fontFamilySecondary',
  'fontSizeBase',
  'headlineStyles',
  'labelStyles',
  'letterSpacingCTA',
  'letterSpacingLabels',
  'partnerDisplayName',
  'partnerLogo',
  'partnerLogoLink',
  'partnershipHeadingLogo',
  'patternBackground',
]

export enum FilterStyleOptions {
  Compact = 'compact',
  Full = 'full',
}

export const getItemFromList = (value, list) => list.find((el) => el.value === value)

export const getValueFromList = (value, list) => {
  const result = getItemFromList(value, list)
  return result ? result.value : undefined
}

export interface DropdownItem<T> {
  label: string
  value: T | ''
}

export const BLANK_STRING = ''

export const ACTIVE_MILITARY_LIST: DropdownItem<ActiveMilitaryOptions>[] = [
  { label: 'Choose one...', value: BLANK_STRING },
  { label: 'Yes', value: ActiveMilitaryOptions.YES },
  { label: 'No', value: ActiveMilitaryOptions.NO },
]

export enum CreditCardSubtypes {
  CreditCard = 'credit_card',
  SecuredCard = 'secured_card',
}

export const CREDIT_SCORE_LIST: DropdownItem<CreditScoreOptions>[] = [
  { label: 'Choose one...', value: BLANK_STRING },
  { label: 'Excellent (720 +)', value: CreditScoreOptions.EXCELLENT },
  { label: 'Good (660 - 719)', value: CreditScoreOptions.GOOD },
  { label: 'Fair (620 - 659)', value: CreditScoreOptions.FAIR },
  { label: 'Poor (< 620)', value: CreditScoreOptions.POOR },
]

export const CO_APPLICANT_TOGGLE_LIST: DropdownItem<string>[] = [
  { label: 'Yes', value: BooleanOptions.True },
  { label: 'No', value: BooleanOptions.False },
]

export const CO_APPLICANT_ADDRESS_OPTIONS: DropdownItem<string>[] = [
  { label: 'Same As Applicant', value: CoApplicantAddressMatch.SameAsApplicant },
  { label: 'Different From Applicant', value: CoApplicantAddressMatch.DifferentFromApplicant },
]

export const COVERAGE_TIER_OPTIONS: DropdownItem<LifeInsuranceCoverageTier>[] = [
  { label: 'Basic', value: LifeInsuranceCoverageTier.Basic },
  { label: 'Essential', value: LifeInsuranceCoverageTier.Essential },
  { label: 'Elite', value: LifeInsuranceCoverageTier.Elite },
]

export const CREDIT_CARD_CREDIT_SCORE_LIST: DropdownItem<CreditScoreOptions>[] = [
  { label: 'Any Credit Score', value: BLANK_STRING },
  { label: 'Excellent (720 +)', value: CreditScoreOptions.EXCELLENT },
  { label: 'Good (660 - 719)', value: CreditScoreOptions.GOOD },
  { label: 'Fair (620 - 659)', value: CreditScoreOptions.FAIR },
  { label: 'Poor (< 620)', value: CreditScoreOptions.POOR },
  { label: 'Limited / No History', value: CreditScoreOptions.LIMITED },
]

export const EDUCATION_LEVEL_LIST: DropdownItem<EducationLevelOptions>[] = [
  { label: 'Choose one...', value: BLANK_STRING },
  { label: 'High School Diploma', value: EducationLevelOptions.HIGH_SCHOOL },
  { label: "Associate's Degree", value: EducationLevelOptions.ASSOCIATES },
  { label: "Bachelor's Degree", value: EducationLevelOptions.BACHELORS },
  { label: "Master's Degree", value: EducationLevelOptions.MASTERS },
  { label: 'Other Graduate Degree', value: EducationLevelOptions.GRADUATE },
  { label: 'Other', value: EducationLevelOptions.OTHER },
]

export enum SLREducationLevelOptions {
  ASSOCIATES = 'associate',
  BACHELORS = 'bachelors',
  MASTERS = 'masters',
  DOCTORATE = 'doctorate',
  HIGH_SCHOOL = 'high_school',
  STILL_ENROLLED = 'still_enrolled',
}

export const SLR_EDUCATION_LEVEL_LIST: DropdownItem<SLREducationLevelOptions>[] = [
  { label: 'Choose one...', value: BLANK_STRING },
  { label: 'High School Diploma', value: SLREducationLevelOptions.HIGH_SCHOOL },
  { label: "Associate's Degree", value: SLREducationLevelOptions.ASSOCIATES },
  { label: "Bachelor's Degree", value: SLREducationLevelOptions.BACHELORS },
  { label: "Master's Degree", value: SLREducationLevelOptions.MASTERS },
  { label: 'Doctorate', value: SLREducationLevelOptions.DOCTORATE },
  { label: 'Still Enrolled', value: SLREducationLevelOptions.STILL_ENROLLED },
]

export const SLR_EDUCATION_LEVEL_BUTTON_LIST: DropdownItem<SLREducationLevelOptions>[] = [
  { label: 'High School Diploma', value: SLREducationLevelOptions.HIGH_SCHOOL },
  { label: "Associate's Degree", value: SLREducationLevelOptions.ASSOCIATES },
  { label: "Bachelor's Degree", value: SLREducationLevelOptions.BACHELORS },
  { label: "Master's Degree", value: SLREducationLevelOptions.MASTERS },
  { label: 'Doctorate', value: SLREducationLevelOptions.DOCTORATE },
  { label: 'Still Enrolled', value: SLREducationLevelOptions.STILL_ENROLLED },
]

export enum GraduateDegreeTypeOptions {
  MD = 'doctor_of_medicine',
  DO = 'doctor_of_optometry',
  DDM = 'doctor_of_dental_medicine',
  DDS = 'doctor_of_dental_surgery',
  DVM = 'doctor_of_veterinary_medicine',
  VMD = 'veterinariae_medicinae_doctoris',
  OTHER = 'other',
}
export const GRADUATE_DEGREE_TYPE_LIST: DropdownItem<GraduateDegreeTypeOptions>[] = [
  { label: 'Choose one...', value: BLANK_STRING },
  { label: 'MD', value: GraduateDegreeTypeOptions.MD },
  { label: 'DO', value: GraduateDegreeTypeOptions.DO },
  { label: 'DDM', value: GraduateDegreeTypeOptions.DDM },
  { label: 'DDS', value: GraduateDegreeTypeOptions.DDS },
  { label: 'DVM', value: GraduateDegreeTypeOptions.DVM },
  { label: 'VMD', value: GraduateDegreeTypeOptions.VMD },
  { label: 'Other', value: GraduateDegreeTypeOptions.OTHER },
]

export const GRADUATE_DEGREE_TYPE_BUTTON_LIST: DropdownItem<GraduateDegreeTypeOptions>[] = [
  { label: 'MD', value: GraduateDegreeTypeOptions.MD },
  { label: 'DO', value: GraduateDegreeTypeOptions.DO },
  { label: 'DDM', value: GraduateDegreeTypeOptions.DDM },
  { label: 'DDS', value: GraduateDegreeTypeOptions.DDS },
  { label: 'DVM', value: GraduateDegreeTypeOptions.DVM },
  { label: 'VMD', value: GraduateDegreeTypeOptions.VMD },
  { label: 'Other', value: GraduateDegreeTypeOptions.OTHER },
]

export const EMPLOYMENT_STATUS_LIST: DropdownItem<EmploymentStatusOptions>[] = [
  { label: 'Choose one...', value: BLANK_STRING },
  { label: 'Employed', value: EmploymentStatusOptions.EMPLOYED },
  { label: 'Military', value: EmploymentStatusOptions.MILITARY },
  { label: 'Not Employed', value: EmploymentStatusOptions.NOT_EMPLOYED },
  { label: 'Retired', value: EmploymentStatusOptions.RETIRED },
  { label: 'Self Employed', value: EmploymentStatusOptions.SELF_EMPLOYED },
  { label: 'Other', value: EmploymentStatusOptions.OTHER },
]

export const LOAN_PURPOSE_LIST: DropdownItem<LoanPurposeOptions>[] = [
  { label: 'Choose one...', value: BLANK_STRING },
  { label: 'Debt Consolidation', value: LoanPurposeOptions.DEBT_CONSOLIDATION },
  { label: 'Credit Card Refinance', value: LoanPurposeOptions.CREDIT_CARD_REFINANCE },
  { label: 'Student Loan Refinance', value: LoanPurposeOptions.STUDENT_REFINANCE },
  { label: 'Home Improvement', value: LoanPurposeOptions.HOME_IMPROVEMENT },
  { label: 'Special Occasion', value: LoanPurposeOptions.SPECIAL_OCCASSION },
  { label: 'Large Purchase', value: LoanPurposeOptions.LARGE_PURCHASE },
  { label: 'Student', value: LoanPurposeOptions.STUDENT },
  { label: 'Auto', value: LoanPurposeOptions.AUTO },
  { label: 'Auto Refinance', value: LoanPurposeOptions.AUTO_REFINANCE },
  { label: 'Baby', value: LoanPurposeOptions.BABY },
  { label: 'Wedding', value: LoanPurposeOptions.WEDDING },
  { label: 'Boat', value: LoanPurposeOptions.BOAT },
  { label: 'Business', value: LoanPurposeOptions.BUSINESS },
  { label: 'Green Loan', value: LoanPurposeOptions.GREEN },
  { label: 'Household Expenses', value: LoanPurposeOptions.HOUSEHOLD_EXPENSE },
  { label: 'Medical and Dental', value: LoanPurposeOptions.MEDICAL },
  { label: 'Moving and Relocation', value: LoanPurposeOptions.MOVING },
  { label: 'Taxes', value: LoanPurposeOptions.TAXES },
  { label: 'Vacation', value: LoanPurposeOptions.VACATION },
  { label: 'Other', value: LoanPurposeOptions.OTHER },
]

export const COMMON_CREDIT_CARD_PURPOSES = [
  { label: 'Balance Transfer', value: CreditCardPurposesOptions.BALANCE_TRANSFER },
  { label: 'Cash Back', value: CreditCardPurposesOptions.CASH_BACK },
  { label: 'Earn Rewards', value: CreditCardPurposesOptions.EARNING_REWARDS },
  { label: 'Improve Credit', value: CreditCardPurposesOptions.IMPROVE_CREDIT },
  { label: 'Low Interest', value: CreditCardPurposesOptions.LOW_INTEREST },
  { label: 'Building Credit', value: CreditCardPurposesOptions.NEW_TO_CREDIT },
  { label: 'Student Card', value: CreditCardPurposesOptions.STUDENT },
  { label: 'Travel Incentives', value: CreditCardPurposesOptions.TRAVEL_INCENTIVES },
]

const creditCardPurposeGroups = [
  { label: 'Earning Rewards', value: CreditCardPurposesOptions.EARNING_REWARDS },
  { label: 'Low Interest Rate', value: CreditCardPurposesOptions.LOW_INTEREST },
  { label: 'Building Credit', value: CreditCardPurposesOptions.NEW_TO_CREDIT },
]

export enum CreditCardFilterTypes {
  CARD_FEATURE = 'card_feature',
  CARD_TYPE = 'card_type',
}

export enum CreditCardFeatureFilterOptionsValues {
  INTRO_BONUS_OFFER = 'intro_bonus_offer',
  NO_ANNUAL_FEE = 'no_annual_fee',
  NO_FOREIGN_TRANSACTION_FEE = 'no_foreign_transaction_fee',
  ZERO_APR = 'zero_apr',
}

enum CreditCardFeatureFilterOptionsLabels {
  INTRO_BONUS_OFFER = 'Intro Bonus Offer',
  NO_ANNUAL_FEE = 'No Annual Fee',
  NO_FOREIGN_TRANSACTION_FEE = 'No Foreign Transaction Fees',
  ZERO_APR = '0% APR',
}

export const CreditCardFeatureFilterOptions = {
  [CreditCardFeatureFilterOptionsValues.INTRO_BONUS_OFFER]: {
    label: CreditCardFeatureFilterOptionsLabels.INTRO_BONUS_OFFER,
    value: CreditCardFeatureFilterOptionsValues.INTRO_BONUS_OFFER,
  },
  [CreditCardFeatureFilterOptionsValues.NO_ANNUAL_FEE]: {
    label: CreditCardFeatureFilterOptionsLabels.NO_ANNUAL_FEE,
    value: CreditCardFeatureFilterOptionsValues.NO_ANNUAL_FEE,
  },
  [CreditCardFeatureFilterOptionsValues.NO_FOREIGN_TRANSACTION_FEE]: {
    label: CreditCardFeatureFilterOptionsLabels.NO_FOREIGN_TRANSACTION_FEE,
    value: CreditCardFeatureFilterOptionsValues.NO_FOREIGN_TRANSACTION_FEE,
  },
  [CreditCardFeatureFilterOptionsValues.ZERO_APR]: {
    label: CreditCardFeatureFilterOptionsLabels.ZERO_APR,
    value: CreditCardFeatureFilterOptionsValues.ZERO_APR,
  },
}

export enum RedemptionTypes {
  FLIGHTS = 'Flights',
  CASH = 'Cash',
  HOTELS = 'Hotels',
  GIFT_CARDS = 'Gift Cards',
  CAR_RENTAL = 'Car Rentals',
  SHOPPING = 'Shopping',
}

export enum CreditCardOfferTabLabels {
  DETAILS = 'Details',
  FOR_YOU = 'For You',
  REWARDS = 'Rewards',
}

export const DEFAULT_REDEMPTION_TYPES: DropdownItem<string>[] = [
  { label: RedemptionTypes.FLIGHTS, value: RewardsPreferenceTypeOptions.FLIGHTS },
  { label: RedemptionTypes.CASH, value: RewardsPreferenceTypeOptions.CASH },
  { label: RedemptionTypes.HOTELS, value: RewardsPreferenceTypeOptions.HOTELS },
  { label: RedemptionTypes.GIFT_CARDS, value: RewardsPreferenceTypeOptions.GIFT_CARDS },
  { label: RedemptionTypes.CAR_RENTAL, value: RewardsPreferenceTypeOptions.CAR_RENTAL },
  { label: RedemptionTypes.SHOPPING, value: RewardsPreferenceTypeOptions.SHOPPING },
]

// TODO make Offer augmenter return enums
export const DEFAULT_REDEMPTION_TYPE_VALUES = {
  [RewardsPreferenceTypeOptions.FLIGHTS]: '0f8e9d16-54dc-11ea-9b3e-0a3037b37e81',
  [RewardsPreferenceTypeOptions.CASH]: '1fc49a17-54dc-11ea-9b3e-0a3037b37e81',
  [RewardsPreferenceTypeOptions.HOTELS]: '106c1fc0-54dc-11ea-9b3e-0a3037b37e81',
  [RewardsPreferenceTypeOptions.GIFT_CARDS]: '13c55d81-54dc-11ea-9b3e-0a3037b37e81',
  [RewardsPreferenceTypeOptions.CAR_RENTAL]: '119c5b0e-54dc-11ea-9b3e-0a3037b37e81',
  [RewardsPreferenceTypeOptions.SHOPPING]: '1e61d3dc-54dc-11ea-9b3e-0a3037b37e81',
}

// CREDIT_CARD_PURPOSE_GROUPS was the name that Product determined these should be
export const CREDIT_CARD_PURPOSE_GROUPS: DropdownItem<CreditCardPurposesOptions>[] = [...creditCardPurposeGroups]

export const CREDIT_CARD_PURPOSES_LIST: DropdownItem<CreditCardPurposesOptions>[] = [
  { label: 'All Card Categories', value: BLANK_STRING },
  ...COMMON_CREDIT_CARD_PURPOSES,
]

export const CREDIT_CARD_PURPOSES_LIST_CHOOSE_ONE: DropdownItem<CreditCardPurposesOptions>[] = [
  { label: 'Choose one...', value: BLANK_STRING },
  ...COMMON_CREDIT_CARD_PURPOSES,
]

export enum OfferOverflowList {
  PAGINATE = 'paginate',
  PARTNER_LINK = 'partner_link',
}

export const NUMBER_OF_UNITS_LIST: DropdownItem<NumberOfUnitsOptions>[] = [
  { label: 'Choose one...', value: BLANK_STRING },
  { label: '1', value: NumberOfUnitsOptions.ONE },
  { label: '2', value: NumberOfUnitsOptions.TWO },
  { label: '3', value: NumberOfUnitsOptions.THREE },
  { label: '4', value: NumberOfUnitsOptions.FOUR },
]

export const PAY_FREQUENCY_LIST: DropdownItem<PayFrequencyOptions>[] = [
  { label: 'Choose one...', value: BLANK_STRING },
  { label: 'Once every week', value: PayFrequencyOptions.WEEKLY },
  { label: 'Once every other week', value: PayFrequencyOptions.BIWEEKLY },
  { label: 'Twice per month', value: PayFrequencyOptions.TWICE_MONTHLY },
  { label: 'Once per month', value: PayFrequencyOptions.MONTHLY },
]

export const PROPERTY_STATUS_LIST: DropdownItem<PropertyStatusOptions>[] = [
  { label: 'Choose one...', value: BLANK_STRING },
  { label: 'Rent', value: PropertyStatusOptions.RENT },
  { label: 'Own with Mortgage', value: PropertyStatusOptions.OWN_WITH_MORTGAGE },
  { label: 'Own', value: PropertyStatusOptions.OWN },
]

export const OCCUPANCY_TYPE_LIST: DropdownItem<OccupancyTypeOptions>[] = [
  { label: 'Choose one...', value: BLANK_STRING },
  { label: 'Primary', value: OccupancyTypeOptions.PRIMARY },
  { label: 'Secondary', value: OccupancyTypeOptions.SECONDARY },
  { label: 'Investment', value: OccupancyTypeOptions.INVESTMENT },
]

export const PURCHASE_STATUS_LIST: DropdownItem<PurchaseStatusOptions>[] = [
  { label: 'Choose one...', value: BLANK_STRING },
  { label: 'No offer', value: PurchaseStatusOptions.NO_OFFER },
  { label: 'Offer pending', value: PurchaseStatusOptions.OFFER_PENDING },
  { label: 'Offer accepted', value: PurchaseStatusOptions.OFFER_ACCEPTED },
  { label: 'Under contract', value: PurchaseStatusOptions.UNDER_CONTRACT },
]
export const PROPERTY_TYPE_LIST: DropdownItem<PropertyTypeOptions>[] = [
  { label: 'Choose one...', value: BLANK_STRING },
  { label: 'Single family', value: PropertyTypeOptions.SINGLE_FAMILY },
  { label: 'Condo', value: PropertyTypeOptions.CONDO },
  { label: 'Townhouse', value: PropertyTypeOptions.TOWN_HOUSE },
]
export const DEPOSITS_PRODUCT_TYPES_LIST: DropdownItem<DepositProductTypesOptions>[] = [
  { label: 'Show All', value: BLANK_STRING },
  { label: 'Online Savings', value: DepositProductTypesOptions.SAVINGS },
  { label: 'Money Market', value: DepositProductTypesOptions.MMA },
  { label: 'Cash Management', value: DepositProductTypesOptions.CASH_MANAGEMENT_ACCOUNT },
  { label: 'Individual Retirement', value: DepositProductTypesOptions.IRA },
  { label: 'Checking', value: DepositProductTypesOptions.CHE },
]

export interface DepositAmountIntentRange {
  maxDepositAmount?: number
  minDepositAmount?: number
}

export const CONVERSION_PROBABILITY_SORTED_COMPANY_UUIDS: string[] = [
  '06ca3349-d461-41c7-9b60-c8d697176ce7',
  '5eeab848-2ab7-43b0-aa78-879868f0a327',
  '64089c72-46f7-4e4a-b668-8b15043ddac5', // cnbc
]

export enum DepositAmountIntentRangeOptions {
  LESS_THAN_1000 = 'less_than_1000',
  BETWEEN_1000_AND_5000 = 'between_1000_5000',
  MORE_THAN_5000 = 'more_than_5000',
}

export const DEPOSITS_AMOUNT_INTENT_RANGE_FILTER_LIST = [
  {
    data: { maxDepositAmount: 999, minDepositAmount: undefined },
    label: 'Less than $1,000',
    value: DepositAmountIntentRangeOptions.LESS_THAN_1000,
  },
  {
    data: { maxDepositAmount: 4999, minDepositAmount: 1000 },
    label: '$1,000 - $5,000',
    value: DepositAmountIntentRangeOptions.BETWEEN_1000_AND_5000,
  },
  {
    data: { maxDepositAmount: undefined, minDepositAmount: 5000 },
    label: '$5,000+',
    value: DepositAmountIntentRangeOptions.MORE_THAN_5000,
  },
]

export const MORTGAGE_TYPE_LIST: DropdownItem<MortgageTypeOptions>[] = [
  { label: 'Choose one...', value: BLANK_STRING },
  { label: 'Purchase', value: MortgageTypeOptions.PURCHASE },
  { label: 'Refinance', value: MortgageTypeOptions.REFINANCE },
]

export const GENDER_OPTION_LIST: DropdownItem<GenderTypeOptions>[] = [
  { label: 'Female', value: GenderTypeOptions.FEMALE },
  { label: 'Male', value: GenderTypeOptions.MALE },
]

export const SLR_US_CITIZEN_OR_GREENCARD_OPTION_LIST: DropdownItem<CitizenshipOptions>[] = [
  { label: 'Choose one...', value: BLANK_STRING },
  { label: 'US Citizen', value: CitizenshipOptions.CITIZEN },
  { label: 'Permanent Resident', value: CitizenshipOptions.PERMANENT_RESIDENT },
  { label: 'Visa Holder', value: CitizenshipOptions.OTHER },
]

export const VEHICLE_ID_TYPE_LIST: DropdownItem<VehicleIdTypeOptions>[] = [
  { label: 'Choose one...', value: BLANK_STRING },
  { label: 'Make/Model', value: VehicleIdTypeOptions.YEAR_MAKE_MODEL_TRIM },
  { label: 'Vehicle Identification Number (VIN)', value: VehicleIdTypeOptions.VEHICLE_IDENTIFICATION_NUMBER },
]

export const HEIGHT_OPTION_LIST: DropdownItem<HeightTypeOptions>[] = [
  { label: 'Choose one...', value: BLANK_STRING },
  { label: '4\'8"', value: HeightTypeOptions.FOUR_FOOT_EIGHT },
  { label: '4\'9"', value: HeightTypeOptions.FOUR_FOOT_NINE },
  { label: '4\'10"', value: HeightTypeOptions.FOUR_FOOT_TEN },
  { label: '4\'11"', value: HeightTypeOptions.FOUR_FOOT_ELEVEN },
  { label: '5\'0"', value: HeightTypeOptions.FIVE_FOOT },
  { label: '5\'1"', value: HeightTypeOptions.FIVE_FOOT_ONE },
  { label: '5\'2"', value: HeightTypeOptions.FIVE_FOOT_TWO },
  { label: '5\'3"', value: HeightTypeOptions.FIVE_FOOT_THREE },
  { label: '5\'4"', value: HeightTypeOptions.FIVE_FOOT_FOUR },
  { label: '5\'5"', value: HeightTypeOptions.FIVE_FOOT_FIVE },
  { label: '5\'6"', value: HeightTypeOptions.FIVE_FOOT_SIX },
  { label: '5\'7"', value: HeightTypeOptions.FIVE_FOOT_SEVEN },
  { label: '5\'8"', value: HeightTypeOptions.FIVE_FOOT_EIGHT },
  { label: '5\'9"', value: HeightTypeOptions.FIVE_FOOT_NINE },
  { label: '5\'10"', value: HeightTypeOptions.FIVE_FOOT_TEN },
  { label: '5\'11"', value: HeightTypeOptions.FIVE_FOOT_ELEVEN },
  { label: '6\'0"', value: HeightTypeOptions.SIX_FOOT },
  { label: '6\'1"', value: HeightTypeOptions.SIX_FOOT_ONE },
  { label: '6\'2"', value: HeightTypeOptions.SIX_FOOT_TWO },
  { label: '6\'3"', value: HeightTypeOptions.SIX_FOOT_THREE },
  { label: '6\'4"', value: HeightTypeOptions.SIX_FOOT_FOUR },
  { label: '6\'5"', value: HeightTypeOptions.SIX_FOOT_FIVE },
  { label: '6\'6"', value: HeightTypeOptions.SIX_FOOT_SIX },
  { label: '6\'7"', value: HeightTypeOptions.SIX_FOOT_SEVEN },
  { label: '6\'8"', value: HeightTypeOptions.SIX_FOOT_EIGHT },
  { label: '6\'9"', value: HeightTypeOptions.SIX_FOOT_NINE },
]

export const DIRECT_DEPOSIT_LIST: DropdownItem<DirectDepositOptions>[] = [
  { label: 'Choose one...', value: BLANK_STRING },
  { label: 'Yes', value: DirectDepositOptions.YES },
  { label: 'No', value: DirectDepositOptions.NO },
]

export const USES_TOBACCO_OPTION_LIST: DropdownItem<TobaccoSmokerOptions>[] = [
  { label: 'Yes', value: TobaccoSmokerOptions.YES },
  { label: 'No', value: TobaccoSmokerOptions.NO },
]

export const US_STATE_ABBREVIATIONS = [
  'AL',
  'AK',
  'AZ',
  'AR',
  'CA',
  'CO',
  'CT',
  'DC',
  'DE',
  'FL',
  'GA',
  'HI',
  'ID',
  'IL',
  'IN',
  'IA',
  'KS',
  'KY',
  'LA',
  'ME',
  'MD',
  'MA',
  'MI',
  'MN',
  'MS',
  'MO',
  'MT',
  'NE',
  'NV',
  'NH',
  'NJ',
  'NM',
  'NY',
  'NC',
  'ND',
  'OH',
  'OK',
  'OR',
  'PA',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VT',
  'VA',
  'WA',
  'WV',
  'WI',
  'WY',
]

export enum AppPrefixQueryParams {
  AppPurpose = 'app.purpose',
}

// Data passed from the Config service
// that will be included in the config provider
export interface ConfigServiceData {
  brand?: Brand
  clientTags?: ClientTags
  embedScripts?: string
  eventScripts?: EventScript[]
  featureFlags?: string[]
  headerText?: string
  headScripts?: string
  loanPurposeOptions?: LoanPurposeOptions[]
  maxResults?: number
  partner?: string
  productType?: ApiProductType
  uiName?: string
  partnerDisclaimer?: string
  pw_fiona?: boolean
  shouldAutoRedirect?: boolean
  shouldDisplayNavigation?: boolean
  company_uuid?: string

  [AppPrefixQueryParams.AppPurpose]?: string

  // Theme
  // TODO: Need to add these in ConfigServiceDataKeys?
  'theme.borderRadiusLarge'?: string
  'theme.borderRadiusSmall'?: string
  'theme.colorBackgroundSecondary'?: string
  'theme.colorBorder'?: string
  'theme.colorCta'?: string
  'theme.colorError'?: string
  'theme.colorPrimary'?: string
  'theme.colorSecondary'?: string
  'theme.colorTextPrimary'?: string
  'theme.colorTextSecondary'?: string
  'theme.fontFamilyPrimary'?: string
  'theme.fontFamilySecondary'?: string
  'theme.fontSizeBase'?: string
  'theme.patternBackground'?: string
  'theme.partnerLogo'?: string
  'theme.partnerLogoLink'?: string
  'theme.partnershipHeadingLogo'?: string
}

export const QueryParamDefaults = {}

export const FlowQueryParamForProduct = {
  [Product.Loan]: QueryParamKeys.LoanFlow,
  [Product.Deposits]: QueryParamKeys.DepositsFlow,
  [Product.CreditCard]: QueryParamKeys.CreditCardFlow,
}
// isFixedSizeEmbed indicates that we are in 'iab mode' which determines a handful of features.
// For example, we want to open offers in a new tab if we are in 'iab mode' but not
// otherwise. The definition of 'is iab mode' currently checks if the query params
// for width and height exist, however in the future we can update this definition
// if it changes, or we want to use width and height to indicate other things than
// IAB mode.
export const isFixedSizeEmbed = () =>
  QueryParams.getAll()[QueryParamKeys.IframeHeight] !== undefined &&
  QueryParams.getAll()[QueryParamKeys.IframeWidth] !== undefined

export const filterStyleCompact = () => QueryParams.getAll()[QueryParamKeys.FilterStyle] === FilterStyleOptions.Compact

export const coAppBadgeBreakpoint = 400

export const DefaultCreditCardFlow = CreditCardFlow.Redirect

export type ProductFlow = CreditCardFlow | LoanFlow

export type ApplicationStep = StepbystepApplicationStep | LifeInsuranceApplicationStep

export const AD_DISCLOSURE_TITLE = `Advertiser Disclosure`

export interface GeocoderResponseArray {
  results: [GeocoderResult]
}

export const PUBLIC_CLASS_NAME_PREFIX = 'even__'

export const PublicClassNames = {
  ApplicationContainer: `${PUBLIC_CLASS_NAME_PREFIX}application`,
  FilterContainer: `${PUBLIC_CLASS_NAME_PREFIX}filters`,
  FormDropdown: `${PUBLIC_CLASS_NAME_PREFIX}form-dropdown`,
  FormInput: `${PUBLIC_CLASS_NAME_PREFIX}form-input`,
  FormLabel: `${PUBLIC_CLASS_NAME_PREFIX}form-label`,
  Modal: `${PUBLIC_CLASS_NAME_PREFIX}modal`,
  Offer: `${PUBLIC_CLASS_NAME_PREFIX}offer`,
  Page: `${PUBLIC_CLASS_NAME_PREFIX}page`,
  SortControls: `${PUBLIC_CLASS_NAME_PREFIX}sort-controls`,
  SortControlsActive: `${PUBLIC_CLASS_NAME_PREFIX}sort-controls-active`,
}

export enum Waypoints {
  LoanInfo = 'loan-info-end-waypoint',
  YourInfo = 'your-info-end-waypoint',
  CoApplicantAddress = 'co-applicant-address-end-waypoint',
  CoApplicantInfo = 'co-applicant-info-end-waypoint',
  ContactInfo = 'contact-info-end-waypoint',
  EmploymentInfo = 'employment-info-end-waypoint',
  SubmitBtn = 'submit-btn-waypoint',
  FinancialInfo = 'financial-info-end-waypoint',
}

export enum TargetTypes {
  CURRENT_WINDOW = '_self',
  NEW_TAB = '_blank',
}

export enum ContactEven {
  Email = 'help@engine.tech',
  Name = 'Engine by MoneyLion',
  Phone = '8004101488',
  Url = 'https://engine.tech',
}

export enum ContactFiona {
  Email = 'help@hifiona.com',
  Name = 'Fiona',
  Phone = '8006147505',
  Url = 'https://fiona.com',
}

export enum ContactLeapLife {
  Email = 'support@leaplife.com',
  Name = 'LeapLife',
  Phone = '8006147505',
  Url = 'https://leaplife.com',
}

export const APR_POSTFIX = '%'
export const FEE_PREFIX = '$'
export const NOT_APPLICABLE = 'Not Applicable'

export enum StepbystepApplicationStep {
  Address = 'address',
  AnnualIncome = 'annualIncome',
  AvailableAssets = 'availableAssets',
  BankRoutingNumber = 'bankRoutingNumber',
  CitizenshipStatus = 'citizenshipStatus',
  CoApplicantAddress = 'coApplicantAddress',
  // eslint-disable-next-line @typescript-eslint/no-shadow
  CoApplicantAddressMatch = 'coApplicantAddressMatch',
  CoApplicantAnnualIncome = 'coApplicantAnnualIncome',
  CoApplicantDateOfBirth = 'coApplicantDateOfBirth',
  CoApplicantName = 'coApplicantName',
  CoApplicantToggle = 'coApplicantToggle',
  Consent = 'consent',
  CreditCardDebt = 'creditCardDebt',
  CreditCardPurposes = 'creditCardPurposes',
  CreditCardRewardsGroupsButtonList = 'creditCardRewardsGroupsButtonList',
  CreditCardRewardsPreference = 'creditCardRewardsPreference',
  CreditCardRedemptionTypes = 'creditCardsRedemptionTypes',
  CreditRating = 'creditRating',
  DateOfBirth = 'dateOfBirth',
  DirectDeposit = 'directDeposit',
  EducationLevel = 'educationLevel',
  Email = 'email',
  EmploymentStatus = 'employmentStatus',
  EstimatedMileage = 'estimatedMileage',
  ExtraQuestionsNotice = 'extraQuestionsNotice',
  GraduateDegreeLevel = 'graduateDegreeLevel',
  GraduationDate = 'graduationDate',
  LoanAmount = 'loanAmount',
  LoanPurpose = 'loanPurpose',
  Name = 'name',
  MonthlyPayment = 'monthlyPayment',
  MonthlyHousingPayment = 'monthlyHousingPayment',
  PayFrequency = 'payFrequency',
  Phone = 'phone',
  PlaidConnect = 'plaidConnect',
  PlaidSummary = 'plaidSummary',
  PropertyStatus = 'propertyStatus',
  SocialSecurity = 'socialSecurity',
  TotalUnsecuredDebt = 'totalUnsecuredDebt',
  University = 'university',
  VehicleIdType = 'vehicleIdType',
  VehicleIdentificationNumber = 'vehicleIdentificationNumber',
  VehicleYearMakeModelAndTrim = 'vehicleYearMakeModelAndTrim',
}

export enum PersonalizedStatus {
  PreApproved = 'preApproved',
  PreQualified = 'preQualified',
  PreSelected = 'preSelected',
}

export const PersonalizedStatuses = Object.keys(PersonalizedStatus).map((key) => PersonalizedStatus[key])

export type PersonalizedStatusText = 'Pre-Approved' | 'Pre-Qualified' | 'Pre-Selected'

type PersonalizedStatusTexts = { [key in PersonalizedStatus]: PersonalizedStatusText }

// eslint-disable-next-line @typescript-eslint/no-redeclare
export const PersonalizedStatusTexts: PersonalizedStatusTexts = {
  [PersonalizedStatus.PreApproved]: 'Pre-Approved',
  [PersonalizedStatus.PreQualified]: 'Pre-Qualified',
  [PersonalizedStatus.PreSelected]: 'Pre-Selected',
}

interface LoanOfferListItemProps<T> {
  bestOfferId?: string
  ctaButtonText?: string
  ctaLabel?: string
  ctaPreQualButtonText?: string
  dispatchOfferCta?: (offer: T) => void
  dispatchSeeMoreFromLender?: (offer: T) => void
  hasMoreOffers?: boolean
  hideCta?: boolean
  isFilteredByLender?: boolean
  isResultsView?: boolean
  leadUuid?: string
  offer: T
  rateTableUuid?: string
  selectedStep?: LoanStep
  showSeeMoreOffers?: boolean
  shouldDisplayTransunionDisclaimer: boolean
  viewportHeight?: number
  viewportWidth?: number
}

export type LoanOfferListItem<T> = React.ReactType<LoanOfferListItemProps<T>>

export type RateTable = DeepReadonly<{
  uuid: string
  leadUuid: string
  lifeInsuranceOffers: LifeInsuranceOffer[]
  loanOffers: LoanOffer[]
  loanAmount?: number
  specialOffers: SpecialOffer[]
  savingsOffers: DepositsOffer[]
  creditCardOffers: CreditOffer[]
  mortgageOffers: MortgageOffer[]
  pendingOriginators?: PendingResponse[]
  pendingResponses?: PendingResponse[]
}>

export enum PlaidStatus {
  NotConnected = 'notConnected', // Before the user has clicked the Plaid button
  Error = 'error',
  Loading = 'loading',
  Success = 'success',
}

export const BEST_EGG_SUPPLY_COMPANY_UUID = '0b5ef1a5-78fb-4be2-8951-14e552f90c14'
export const BEST_EGG_DEMAND_COMPANY_UUID = 'e8321746-1e5f-4981-bca6-be5e84782528'

export const BUSINESS_INSIDER_COMPANY_UUID = '1829c16a-1a96-4f68-8dcb-17bb435f79d2'

export const CNBC_COMPANY_UUID = '64089c72-46f7-4e4a-b668-8b15043ddac5'

export const TRANSUNION_COMPANY_UUID = 'c0cf43ab-a507-43bb-aed8-4be04219c187'

export const SEED_FI_ORIGINATOR_ID = 'seedFi'

export const ML_CREDIT_BUILDER_PLUS_ID = 'moneyLionCreditBuilderPlus'

export const TPH_AMEX_COMPANY_UUID = 'd493877e-6add-49b5-a0d5-ecfe12aec300'

export const TPH_LIFE_INSURANCE_COMPANY_UUID = 'd4f4cc75-9292-438a-8933-60734ef5142e'

export const CREDIT_CARD_DEBT_COMPANY_UUIDS = [
  'fcc19811-7f72-4dc9-a390-80d8f52ed21b',
  '5eeab848-2ab7-43b0-aa78-879868f0a327',
  'a86adf41-48b1-4d08-a928-b468f1eb30a6',
  'd766c7fd-7961-4b9b-8cb0-f2f3725a5f1c',
  '74aa3b44-3402-4322-afcb-fc44f0196689',
  '405da511-6b66-40a1-8221-203bb416b7e6',
  '345ce360-269f-4b3d-8bf3-e79e8053ff40',
  '55e4fad8-6a7b-443d-9db1-9cbba8c76694',
  '9b49cbce-789c-4c96-b383-7d0b7ef0b2d0',
  'fe58d356-09c2-4db9-973f-183079f18eb5',
  '54a05653-d745-4be2-9ec2-57eb7298b90f',
  'edaa9844-2837-4299-bf7d-c707cc337a7c',
  'ee6f467f-d437-4834-9430-83a5b411858f',
  '053a75bf-0876-43f8-9d26-2dae91011e0f',
  'f821f4fa-fe42-4f8b-98e0-6bfcdd0a1231',
  '84258043-c6ca-4f2d-91e8-754aee30ea6a',
  '7603c407-b737-48d4-a71f-e5d5289e7fe9',
  '3999404c-9f4f-4654-b625-73436d360d06',
  'b9225f95-3d98-4232-8bdf-8e508e73f3dc',
  '54a05653-d745-4be2-9ec2-57eb7298b90f',
  '23854080-4f14-45da-b408-1eb9bf2b9dc4',
  'fe58d356-09c2-4db9-973f-183079f18eb5',
  'd74c1a2b-d743-4dfc-b234-22def3edf7fb',
  '9b49cbce-789c-4c96-b383-7d0b7ef0b2d0',
  '345ce360-269f-4b3d-8bf3-e79e8053ff40',
  'a86adf41-48b1-4d08-a928-b468f1eb30a6',
]

export const lifeInsuranceDemandSubAccountIdsToFilter = [1568, 1309, 1995, 1694, 1726, 1989, 1879, 1548, 1456]

export const FetchRateTableDebounceWait = 1000

export const EMPOWER_PROD_COMPANY_UUID = '92f83b4d-ef95-407e-942c-a35ed5136302'

export const isCreditCardDebtCompany = (companyUuid?: string) =>
  companyUuid && CREDIT_CARD_DEBT_COMPANY_UUIDS.includes(companyUuid)

// @TODO: update params to satisfy linter requirement or comment to why this needs to stay as is
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const noop = (..._: unknown[]): any => {}

export enum LifeInsuranceApplicationStep {
  DateOfBirth = 'dateOfBirth',
  Gender = 'gender',
  HeightInInches = 'heightInInches',
  WeightInPounds = 'weightInPounds',
  UsesTobacco = 'usesTobacco',
  RouterQuestions = 'routerQuestions',
  StateOfResidence = 'stateOfResidence',
}

export type MappedLifeInsuranceQuestions = DeepReadonly<{
  [key: string]: LifeInsuranceQuestion
}>

export type LifeInsuranceQuestionGroups = DeepReadonly<string[][]>

export const LifeInsuranceQuestionFreeform = new Set([
  LifeInsuranceQuestionType.FreeformMultiple,
  LifeInsuranceQuestionType.FreeformOne,
  LifeInsuranceQuestionType.PrescriptionAutocomplete,
])

export type CommonDispatch = ThunkDispatch<CommonState, void, Action>

const uuidRegExp = /^[0-9a-f]{8}\-[0-9a-f]{4}\-[0-9a-f]{4}\-[0-9a-f]{4}\-[0-9a-f]{12}$/i
export const isUuid = (str: string) => uuidRegExp.test(str)

export enum UNSECURED_DEBT_RESPONSES_VALUES {
  NoDebt = 'NoDebt',
  UnderTenThousand = 'UnderTenThousand',
  TenThousandToNearlyTwenty = 'TenThousandToNearlyTwenty',
  TwentyThousandToNearlyThirty = 'TwentyThousandToNearlyThirty',
  ThirtyThousandToNearlyForty = 'ThirtyThousandToNearlyForty',
  FortyThousandOrMore = 'FortyThousandOrMore',
}

export const UNSECURED_DEBT_RESPONSES_LIST = [
  { label: 'None', value: UNSECURED_DEBT_RESPONSES_VALUES.NoDebt },
  { label: '$1 - $9,999', value: UNSECURED_DEBT_RESPONSES_VALUES.UnderTenThousand },
  { label: '$10,000 - $19,999', value: UNSECURED_DEBT_RESPONSES_VALUES.TenThousandToNearlyTwenty },
  { label: '$20,000 - $29,999', value: UNSECURED_DEBT_RESPONSES_VALUES.TwentyThousandToNearlyThirty },
  { label: '$30,000 - $39,999', value: UNSECURED_DEBT_RESPONSES_VALUES.ThirtyThousandToNearlyForty },
  { label: '$40,000 or more', value: UNSECURED_DEBT_RESPONSES_VALUES.FortyThousandOrMore },
]

export * from './pii-handling'
